import React, { useState, useEffect } from 'react';
import { ArrowDown, AlertCircle, Loader, Settings, ExternalLink, ArrowLeft } from 'lucide-react';
import { useWallet } from './WalletContext';
import { Link } from 'react-router-dom';
import oneInchService from './oneInchService';

const SwapExecutor = () => {
  const { isConnected, walletAddress, connect, getProvider } = useWallet();
  
  const [fromToken, setFromToken] = useState('ETH');
  const [toToken, setToToken] = useState('USDC');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [selectedChain, setSelectedChain] = useState('ETHEREUM');
  const [estimatedGas, setEstimatedGas] = useState('');
  const [protocols, setProtocols] = useState([]);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState('');
  const [slippage, setSlippage] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [availableTokens, setAvailableTokens] = useState([]);

  const chains = {
    'ETHEREUM': { id: '0x1', name: 'Ethereum', explorer: 'https://etherscan.io' },
    'ARBITRUM': { id: '0xa4b1', name: 'Arbitrum', explorer: 'https://arbiscan.io' },
    'OPTIMISM': { id: '0xa', name: 'Optimism', explorer: 'https://optimistic.etherscan.io' },
    'BASE': { id: '0x2105', name: 'Base', explorer: 'https://basescan.org' }
  };

  const tokens = availableTokens.length > 0 ? availableTokens : [
    { symbol: 'ETH', name: 'Ethereum', decimals: 18 },
    { symbol: 'USDC', name: 'USD Coin', decimals: 6 },
    { symbol: 'USDT', name: 'Tether', decimals: 6 },
    { symbol: 'DAI', name: 'Dai', decimals: 18 },
    { symbol: 'WETH', name: 'Wrapped Ether', decimals: 18 }
  ];

  useEffect(() => {
    fetchAvailableTokens();
  }, []);

  const fetchAvailableTokens = async () => {
    try {
      const response = await fetch('/api/tokens');
      if (response.ok) {
        const data = await response.json();
        const tokenList = Object.entries(data.tokens).map(([symbol, info]) => ({
          symbol: symbol,
          name: info.name,
          decimals: ['USDC', 'USDT', 'BUSD', 'FRAX'].includes(symbol) ? 6 : 
                    symbol === 'WBTC' ? 8 : 18
        }));
        setAvailableTokens(tokenList);
      }
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (fromAmount && parseFloat(fromAmount) > 0) {
        getSwapQuote();
      } else {
        setToAmount('');
        setEstimatedGas('');
        setProtocols([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fromAmount, fromToken, toToken, selectedChain, slippage]);

  useEffect(() => {
    if (isConnected && fromToken !== 'ETH') {
      checkApproval();
    } else {
      setNeedsApproval(false);
    }
  }, [fromToken, isConnected, walletAddress]);

  const getSwapQuote = async () => {
    setIsLoadingQuote(true);
    setError('');
    
    try {
      const quote = await oneInchService.getQuote(
        fromToken,
        toToken,
        fromAmount,
        selectedChain,
        slippage
      );
      
      setToAmount(quote.toAmount.toString());
      setEstimatedGas(quote.estimatedGas || 'N/A');
      
      if (quote.protocols && quote.protocols.length > 0) {
        const protocolNames = quote.protocols.flat().map(p => p[0]?.name).filter(Boolean);
        setProtocols([...new Set(protocolNames)]);
      }
    } catch (err) {
      console.error('Quote error:', err);
      
      let errorMessage = 'Failed to get quote';
      
      if (err.message && err.message.includes('not found')) {
        errorMessage = `Unable to fetch price for ${fromToken} → ${toToken}. This usually means there's not enough liquidity or a temporary API issue. Try refreshing or selecting different tokens.`;
      } else if (err.message && err.message.includes('insufficient')) {
        errorMessage = `Insufficient ${fromToken} balance in your wallet.`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setToAmount('');
      setEstimatedGas('');
      setProtocols([]);
    } finally {
      setIsLoadingQuote(false);
    }
  };

  const checkApproval = async () => {
    if (!isConnected || fromToken === 'ETH') {
      setNeedsApproval(false);
      return;
    }
    
    try {
      const tokenAddress = oneInchService.getTokenAddress(selectedChain, fromToken);
      
      if (!tokenAddress) {
        return;
      }

      const allowance = await oneInchService.checkAllowance(
        tokenAddress,
        walletAddress,
        selectedChain
      );
      
      const requiredAmount = oneInchService.toWei(
        fromAmount || '0',
        fromToken === 'USDC' || fromToken === 'USDT' ? 6 : 18
      );
      
      const needsApprovalCheck = allowance === '0' || 
        parseFloat(allowance) < parseFloat(requiredAmount);
      
      setNeedsApproval(needsApprovalCheck);
    } catch (err) {
      setNeedsApproval(true);
    }
  };

  const handleApprove = async () => {
    if (!isConnected) {
      await connect();
      return;
    }

    setIsApproving(true);
    setError('');

    try {
      console.log('Starting approval for', fromToken, 'on', selectedChain);
      
      const approvalTx = await oneInchService.getApprovalTransaction(
        fromToken,
        selectedChain
      );

      console.log('Approval transaction:', approvalTx);
      console.log('Sending transaction...');

      const provider = getProvider();
      if (!provider) {
        throw new Error('Wallet not connected');
      }

      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{
          from: walletAddress,
          to: approvalTx.to,
          data: approvalTx.data,
          value: approvalTx.value || '0x0',
          gasPrice: approvalTx.gasPrice
        }]
      });

      console.log('Transaction hash:', txHash);
      setTxHash(txHash);
      await waitForTransaction(txHash);
      
      setNeedsApproval(false);
      setTxHash('');
      alert(`${fromToken} approved successfully!`);
    } catch (err) {
      console.error('Approval error:', err);
      
      if (err.code === 4001) {
        setError('Approval rejected by user');
      } else if (err.message && err.message.includes('insufficient funds')) {
        setError(`Insufficient ETH for gas fees. You need ETH in your wallet to pay for the approval transaction.`);
      } else if (err.code === -32000) {
        setError('Insufficient funds for gas. Please add ETH to your wallet.');
      } else {
        setError(err.message || 'Approval failed. Make sure you have enough ETH for gas fees.');
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleSwap = async () => {
    if (!isConnected) {
      await connect();
      return;
    }

    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      setError('Please enter an amount');
      return;
    }

    if (!toAmount || parseFloat(toAmount) <= 0) {
      setError('Invalid quote');
      return;
    }

    setIsSwapping(true);
    setError('');
    setTxHash('');

    try {
      const swapTx = await oneInchService.getSwapTransaction(
        fromToken,
        toToken,
        fromAmount,
        walletAddress,
        selectedChain,
        slippage
      );

      const provider = getProvider();
      if (!provider) {
        throw new Error('Wallet not connected');
      }

      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{
          from: walletAddress,
          to: swapTx.to,
          data: swapTx.data,
          value: swapTx.value || '0x0',
          gas: swapTx.gas,
          gasPrice: swapTx.gasPrice
        }]
      });

      setTxHash(txHash);
      await waitForTransaction(txHash);
      
      alert(`Swap successful! You received approximately ${swapTx.toAmount} ${toToken}`);
      
      setFromAmount('');
      setToAmount('');
      setTxHash('');
      setEstimatedGas('');
      setProtocols([]);
    } catch (err) {
      if (err.code === 4001) {
        setError('Transaction rejected by user');
      } else {
        setError(err.message || 'Swap failed');
      }
      setTxHash('');
    } finally {
      setIsSwapping(false);
    }
  };

  const waitForTransaction = async (txHash) => {
    const provider = getProvider();
    if (!provider) {
      throw new Error('Wallet not connected');
    }
    
    return new Promise((resolve) => {
      const checkInterval = setInterval(async () => {
        try {
          const receipt = await provider.request({
            method: 'eth_getTransactionReceipt',
            params: [txHash]
          });

          if (receipt) {
            clearInterval(checkInterval);
            if (receipt.status === '0x1') {
              resolve(receipt);
            } else {
              resolve();
            }
          }
        } catch (err) {
          console.error('Error checking transaction:', err);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 300000);
    });
  };

  const switchTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  const getExplorerUrl = (txHash) => {
    const explorer = chains[selectedChain]?.explorer || 'https://etherscan.io';
    return `${explorer}/tx/${txHash}`;
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="fixed top-4 right-4 z-50">
          {isConnected ? (
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-[#476bff] rounded-xl px-4 py-2 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <div>
                  <p className="text-xs text-gray-400">Connected</p>
                  <p className="text-sm font-bold text-white font-mono">
                    {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={connect}
              className="bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-6 py-3 font-bold transition shadow-lg"
            >
              Connect Wallet
            </button>
          )}
        </div>

        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Back to Home</span>
        </Link>

        <h1 className="text-4xl font-black text-white mb-2 text-center">Token Swap</h1>
        <p className="text-gray-400 text-center mb-8">Swap tokens across multiple DEXes</p>

        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-6 border border-gray-800">
          
          <div className="mb-6">
            <label className="block text-sm font-bold text-[#50ffff] mb-2">Chain</label>
            <select
              value={selectedChain}
              onChange={(e) => setSelectedChain(e.target.value)}
              className="w-full bg-black border-2 border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#476bff] outline-none"
            >
              {Object.entries(chains).map(([key, chain]) => (
                <option key={key} value={key}>{chain.name}</option>
              ))}
            </select>
          </div>

          <div className="bg-black border-2 border-gray-700 rounded-xl p-4 mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">From</span>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="text-gray-400 hover:text-white"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-3">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0.0"
                className="flex-1 bg-transparent text-3xl font-bold text-white outline-none"
                step="any"
              />
              <select
                value={fromToken}
                onChange={(e) => setFromToken(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-bold outline-none"
              >
                {tokens.map(token => (
                  <option key={token.symbol} value={token.symbol}>{token.symbol}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-center -my-1 relative z-10">
            <button
              onClick={switchTokens}
              className="bg-gray-800 border-2 border-gray-700 hover:border-[#476bff] rounded-xl p-2 transition"
            >
              <ArrowDown className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="bg-black border-2 border-gray-700 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">To</span>
              {isLoadingQuote && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader className="w-3 h-3 animate-spin" />
                  Getting quote...
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                value={toAmount}
                readOnly
                placeholder="0.0"
                className="flex-1 bg-transparent text-3xl font-bold text-white outline-none"
              />
              <select
                value={toToken}
                onChange={(e) => setToToken(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-bold outline-none"
              >
                {tokens.map(token => (
                  <option key={token.symbol} value={token.symbol}>{token.symbol}</option>
                ))}
              </select>
            </div>
          </div>

          {showSettings && (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-bold text-white mb-3">Slippage Tolerance</h3>
              <div className="flex gap-2">
                {[0.1, 0.5, 1.0].map(val => (
                  <button
                    key={val}
                    onClick={() => setSlippage(val)}
                    className={`px-4 py-2 rounded-lg font-bold text-sm ${
                      slippage === val
                        ? 'bg-[#476bff] text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {val}%
                  </button>
                ))}
                <input
                  type="number"
                  value={slippage}
                  onChange={(e) => setSlippage(parseFloat(e.target.value) || 0.5)}
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm outline-none"
                  placeholder="Custom"
                  step="0.1"
                  min="0.1"
                  max="50"
                />
              </div>
            </div>
          )}

          {toAmount && fromAmount && parseFloat(toAmount) > 0 && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rate</span>
                <span className="text-white font-semibold">
                  1 {fromToken} = {(parseFloat(toAmount) / parseFloat(fromAmount)).toFixed(6)} {toToken}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Slippage</span>
                <span className="text-white font-semibold">{slippage}%</span>
              </div>
              {estimatedGas && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Est. Gas</span>
                  <span className="text-white font-semibold">{estimatedGas}</span>
                </div>
              )}
              {protocols.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Route</span>
                  <span className="text-white font-semibold text-right">
                    {protocols.slice(0, 3).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {error && !error.includes('Available tokens') && (
            <div className="bg-red-900/30 border border-red-500 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {txHash && (
            <div className="bg-blue-900/30 border border-blue-500 rounded-xl p-4 mb-4 flex items-start gap-3">
              <Loader className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5 animate-spin" />
              <div className="flex-1">
                <p className="text-sm text-blue-300 mb-1">Transaction pending...</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-blue-400 font-mono break-all flex-1">{txHash}</p>
                  <a
                    href={getExplorerUrl(txHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {!isConnected ? (
            <button
              onClick={connect}
              className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-6 py-4 font-bold transition text-lg"
            >
              Connect Wallet
            </button>
          ) : needsApproval && fromToken !== 'ETH' ? (
            <button
              onClick={handleApprove}
              disabled={isApproving || !fromAmount}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:opacity-90 text-white rounded-xl px-6 py-4 font-bold transition text-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isApproving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Approving {fromToken}...
                </>
              ) : (
                `Approve ${fromToken}`
              )}
            </button>
          ) : (
            <button
              onClick={handleSwap}
              disabled={isSwapping || !fromAmount || !toAmount || parseFloat(toAmount) <= 0}
              className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-6 py-4 font-bold transition text-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSwapping ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Swapping...
                </>
              ) : (
                'Swap Tokens'
              )}
            </button>
          )}

          <div className="mt-4 bg-blue-900/20 border border-blue-500/30 rounded-xl p-3">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-300">
                <strong>Powered by 1inch:</strong> This interface executes real swaps on the blockchain using 1inch aggregator for best prices across 50+ DEXes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwapExecutor;